import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api, API } from "@/lib/apiClient";
import { ytId } from "./WeeklyMessage";
import { CalendarPlus, PlayCircle } from "lucide-react";

function EventSlot({ event, index }) {
  if (!event) {
    return (
      <div className="glass rounded-3xl p-3 flex flex-col" data-testid={`event-slot-empty-${index}`}>
        <div className="relative flex-1 min-h-[240px] w-full rounded-2xl border border-dashed border-white/15 bg-white/[0.02]">
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-[#7A708A] px-4 text-center">
            <CalendarPlus className="w-7 h-7" strokeWidth={1.2} />
            <p className="text-sm font-light">Upcoming event coming soon</p>
          </div>
        </div>
      </div>
    );
  }

  const fileUrl = event.file_path ? `${API}/events/file/${event.id}` : null;
  const vid = event.youtube_url ? ytId(event.youtube_url) : null;

  return (
    <div className="glass rounded-3xl p-3 flex flex-col" data-testid={`event-slot-${index}`}>
      <div className="relative w-full overflow-hidden rounded-2xl bg-black/40" style={{ paddingBottom: "125%" }}>
        {event.media_type === "video" && vid && (
          <iframe
            src={`https://www.youtube.com/embed/${vid}`}
            title={event.title}
            className="absolute inset-0 w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        )}
        {event.media_type === "video" && !vid && (
          <a href={event.youtube_url} target="_blank" rel="noreferrer" className="absolute inset-0 flex items-center justify-center text-gold gap-2">
            <PlayCircle className="w-6 h-6" /> Watch
          </a>
        )}
        {event.media_type === "flyer" && (
          <img src={fileUrl} alt={event.title} className="absolute inset-0 w-full h-full object-contain" />
        )}
        {event.media_type === "audio" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 px-4">
            <PlayCircle className="w-10 h-10 text-gold" strokeWidth={1.2} />
            <audio controls src={fileUrl} className="w-full" data-testid={`event-audio-${index}`} />
          </div>
        )}
      </div>
      <div className="px-2 pt-3 pb-1 flex-1 flex flex-col">
        <p className="text-white font-display text-base leading-snug">{event.title}</p>
        {event.note && <p className="text-xs text-[#B4ADC1] font-light mt-1 leading-relaxed">{event.note}</p>}
      </div>
    </div>
  );
}

export default function UpcomingEvents() {
  const [events, setEvents] = useState([]);

  useEffect(() => {
    api.get("/events").then(({ data }) => setEvents(data)).catch(() => {});
  }, []);

  const slots = [0, 1, 2, 3].map((i) => events[i] || null);

  return (
    <section id="events" className="relative bg-[#05030A] pb-20 px-5 sm:px-8">
      <div className="relative max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1, ease: [0.22, 1, 0.36, 1] }}
          className="text-center"
        >
          <span className="section-label">Upcoming Events</span>
          <h2 className="font-display text-2xl sm:text-3xl font-light text-white mt-3">
            What's happening at <span className="italic text-gold">F.O.L.W.A.C.</span>
          </h2>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15, ease: [0.22, 1, 0.36, 1] }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10"
          data-testid="upcoming-events-grid"
        >
          {slots.map((ev, i) => (
            <EventSlot key={ev ? ev.id : `empty-${i}`} event={ev} index={i} />
          ))}
        </motion.div>
      </div>
    </section>
  );
}
